import AddressFieldLabels from '../types/AddressFieldLabels';

/**
 * Address field labels for different countries
 * Provides localized terminology for address fields based on country
 */

/**
 * Default address field labels (used as fallback)
 */
export const DEFAULT_ADDRESS_LABELS: AddressFieldLabels = {
  street1: 'Street Address',
  street2: 'Apt, Suite, etc.',
  city: 'City',
  state: 'State/Province/Region',
  postalCode: 'Postal Code',
};

/**
 * Country-specific address field labels
 * Key is ISO 3166-1 alpha-2 country code
 */
export const COUNTRY_ADDRESS_LABELS: Record<string, AddressFieldLabels> = {
  // United States
  US: {
    street1: 'Street Address',
    street2: 'Apt, Suite, etc.',
    city: 'City',
    state: 'State',
    postalCode: 'ZIP Code',
  },

  // Canada
  CA: {
    street1: 'Street Address',
    street2: 'Apt, Suite, etc.',
    city: 'City',
    state: 'Province',
    postalCode: 'Postal Code',
  },

  // United Kingdom
  GB: {
    street1: 'Address Line 1',
    street2: 'Address Line 2',
    city: 'Town/City',
    state: 'County',
    postalCode: 'Postcode',
  },

  // Australia
  AU: {
    street1: 'Street Address',
    street2: 'Apt, Unit, etc.',
    city: 'Suburb',
    state: 'State/Territory',
    postalCode: 'Postcode',
  },

  // Ireland
  IE: {
    street1: 'Address Line 1',
    street2: 'Address Line 2',
    city: 'Town/City',
    state: 'County',
    postalCode: 'Eircode',
  },

  // New Zealand
  NZ: {
    street1: 'Street Address',
    street2: 'Apt, Unit, etc.',
    city: 'Suburb',
    state: 'Region',
    postalCode: 'Postcode',
  },

  // India
  IN: {
    street1: 'Address Line 1',
    street2: 'Address Line 2',
    city: 'City',
    state: 'State',
    postalCode: 'PIN Code',
  },

  // Mexico
  MX: {
    street1: 'Calle y Número',
    street2: 'Col, Depto, etc.',
    city: 'Ciudad',
    state: 'Estado',
    postalCode: 'Código Postal',
  },

  // Brazil
  BR: {
    street1: 'Endereço',
    street2: 'Complemento',
    city: 'Cidade',
    state: 'Estado',
    postalCode: 'CEP',
  },

  // Germany
  DE: {
    street1: 'Straße und Hausnummer',
    street2: 'Adresszusatz',
    city: 'Stadt',
    state: 'Bundesland',
    postalCode: 'Postleitzahl',
  },

  // France
  FR: {
    street1: 'Adresse',
    street2: 'Complément d\'adresse',
    city: 'Ville',
    state: 'Région',
    postalCode: 'Code Postal',
  },

  // Spain
  ES: {
    street1: 'Dirección',
    street2: 'Piso, Puerta, etc.',
    city: 'Ciudad',
    state: 'Provincia',
    postalCode: 'Código Postal',
  },

  // Italy
  IT: {
    street1: 'Indirizzo',
    street2: 'Int, Scala, etc.',
    city: 'Città',
    state: 'Provincia',
    postalCode: 'CAP',
  },

  // Netherlands
  NL: {
    street1: 'Straat en Huisnummer',
    street2: 'Aanvulling',
    city: 'Plaats',
    state: 'Provincie',
    postalCode: 'Postcode',
  },

  // Japan
  JP: {
    street1: 'Street Address',
    street2: 'Building, Room, etc.',
    city: 'City/Ward',
    state: 'Prefecture',
    postalCode: 'Postal Code',
  },

  // China
  CN: {
    street1: 'Street Address',
    street2: 'Building, Room, etc.',
    city: 'City',
    state: 'Province',
    postalCode: 'Postal Code',
  },

  // South Korea
  KR: {
    street1: 'Street Address',
    street2: 'Building, Room, etc.',
    city: 'City/District',
    state: 'Province',
    postalCode: 'Postal Code',
  },

  // Singapore
  SG: {
    street1: 'Street Address',
    street2: 'Unit Number',
    city: 'District',
    state: 'Region',
    postalCode: 'Postal Code',
  },

  // South Africa
  ZA: {
    street1: 'Street Address',
    street2: 'Complex, Unit, etc.',
    city: 'City/Town',
    state: 'Province',
    postalCode: 'Postal Code',
  },
};

/**
 * Get address field labels for a specific country
 * Falls back to default labels if country not found
 */
export const getAddressLabels = (countryCode: string): AddressFieldLabels => {
  return COUNTRY_ADDRESS_LABELS[countryCode] || DEFAULT_ADDRESS_LABELS;
};

/**
 * Check if a country typically uses a state/province field
 * Some countries don't have state/province subdivisions
 */
export const countryUsesState = (countryCode: string): boolean => {
  const noStateCountries = [
    'SG', // Singapore
    'HK', // Hong Kong
    'MO', // Macao
    'VA', // Vatican City
    'MC', // Monaco
    'LI', // Liechtenstein
    'MT', // Malta
    'IS', // Iceland
  ];
  return !noStateCountries.includes(countryCode);
};

/**
 * Check if a country requires a postal code
 * Some countries don't use postal codes
 */
export const countryRequiresPostalCode = (countryCode: string): boolean => {
  const noPostalCodeCountries = [
    'AE', // United Arab Emirates (optional)
    'AG', // Antigua and Barbuda
    'AO', // Angola
    'BS', // Bahamas
    'BZ', // Belize
    'BJ', // Benin
    'BW', // Botswana
    'BF', // Burkina Faso
    'BI', // Burundi
    'CM', // Cameroon
    'CF', // Central African Republic
    'KM', // Comoros
    'CG', // Congo
    'CD', // Congo, Democratic Republic
    'CK', // Cook Islands
    'CI', // Côte d'Ivoire
    'DJ', // Djibouti
    'DM', // Dominica
    'GQ', // Equatorial Guinea
    'ER', // Eritrea
    'FJ', // Fiji
    'GM', // Gambia
    'GH', // Ghana
    'GD', // Grenada
    'GY', // Guyana
    'HK', // Hong Kong (optional)
    'IE', // Ireland (Eircode is optional)
    'JM', // Jamaica
    'KI', // Kiribati
    'MO', // Macao
    'MW', // Malawi
    'ML', // Mali
    'MR', // Mauritania
    'NR', // Nauru
    'NU', // Niue
    'KP', // North Korea
    'PA', // Panama (optional)
    'QA', // Qatar
    'RW', // Rwanda
    'KN', // Saint Kitts and Nevis
    'LC', // Saint Lucia
    'ST', // São Tomé and Príncipe
    'SC', // Seychelles
    'SL', // Sierra Leone
    'SB', // Solomon Islands
    'SO', // Somalia
    'SR', // Suriname
    'SY', // Syria
    'TL', // Timor-Leste
    'TK', // Tokelau
    'TO', // Tonga
    'TT', // Trinidad and Tobago
    'TV', // Tuvalu
    'UG', // Uganda
    'VU', // Vanuatu
    'YE', // Yemen
    'ZW', // Zimbabwe
  ];
  return !noPostalCodeCountries.includes(countryCode);
};
